import {
  keepPreviousData,
  queryOptions,
  useQuery,
  type UseQueryResult,
} from "@tanstack/react-query";

import { useDomainServices } from "@/providers/DomainServices";

import type { QueryService } from "../QueryService";

/**
 * Query options for a submitted query's total row count. Extracted from the
 * hook so a route loader can prefetch into the same cache entry.
 */
export function queryResultCountOptions(
  queryService: QueryService,
  queryId: string | undefined,
) {
  return queryOptions({
    queryKey: ["queryResultCount", queryId],
    queryFn: ({ signal }) =>
      queryService.getQueryResultCount(queryId!, { abortSignal: signal }),
    enabled: !!queryId,
    staleTime: Infinity,
  });
}

/**
 * Total row count for a submitted query. Independent failure mode from the
 * results feed — count failures should not hide rows; consumers can ignore
 * `error` and treat `data === undefined` as "count not available."
 */
export function useQueryCount(
  queryId: string | undefined,
): UseQueryResult<number, Error> {
  const { queryService } = useDomainServices();

  return useQuery({
    ...queryResultCountOptions(queryService, queryId),
    placeholderData: keepPreviousData,
  });
}
